import React, { useContext } from "react";
import {
    PaginationContainer,
    Summary,
    PaginationNav,
    PageButton,
    ArrowButton,
    RowsSection,
    RowsLabel,
    RowsSelect,
} from "./Pagination.styles";
import { ApplicationContext } from "../../../components/ApplicationProvider";

type PaginationProps = {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    rowsPerPage: number;
    itemLabel?: string;
    startItem?: number;
    endItem?: number;

    onPageChange?: (page: number) => void;
    onRowsChange?: (rows: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    itemLabel,
    startItem,
    endItem,
    onPageChange,
    onRowsChange,
}) => {
    const { t } = useContext(ApplicationContext);
    const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
    const label = itemLabel ?? t(totalItems === 1 ? "pagination.item" : "pagination.items");
    const ROWS_PER_PAGE_OPTIONS = [6, 9, 12, 15];

    const summaryStartItem =
        startItem ?? (totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1);
    const summaryEndItem = endItem ?? Math.min(totalItems, currentPage * rowsPerPage);

    const handleRowsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onRowsChange?.(Number(e.target.value));
    };

    return (
        <PaginationContainer>
            <Summary>
                {String(summaryStartItem).padStart(2, "0")}-
                {String(summaryEndItem).padStart(2, "0")} of {String(totalItems).padStart(2, "0")}{" "}
                {label}
            </Summary>
            <PaginationNav>
                <ArrowButton
                    aria-label="First page"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange?.(1)}
                >
                    {"<<"}
                </ArrowButton>
                <ArrowButton
                    aria-label="Previous page"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                >
                    {"<"}
                </ArrowButton>
                {pages.map((page) => (
                    <PageButton
                        key={page}
                        active={page === currentPage}
                        aria-current={page === currentPage ? "page" : undefined}
                        onClick={() => onPageChange?.(page)}
                    >
                        {page}
                    </PageButton>
                ))}

                <ArrowButton
                    aria-label="Next page"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
                >
                    {">"}
                </ArrowButton>
                <ArrowButton
                    aria-label="Last page"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange?.(totalPages)}
                >
                    {">>"}
                </ArrowButton>
            </PaginationNav>
            <RowsSection>
                <RowsLabel as="label" htmlFor="pagination-rows-per-page">
                    {t("pagination.results_per_page")}
                </RowsLabel>
                <RowsSelect
                    id="pagination-rows-per-page"
                    value={rowsPerPage}
                    onChange={handleRowsChange}
                >
                    {ROWS_PER_PAGE_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                            {value}
                        </option>
                    ))}
                </RowsSelect>
            </RowsSection>
        </PaginationContainer>
    );
};

export default Pagination;
